/**
 * The AI page: what the `ai` service does (calls, errors, cost, speed) and the controls for it (the
 * order of the routes of each model, aliases, deny rules, and the settings
 * and kill switch of each organization), plus the monthly spend limits and this month's spend of billing. Platform admins only. Every write has an audit row in
 * `ai`, shown on the Changes tab.
 */
import { useSearchParams } from 'react-router-dom';

import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { aiApi } from '@/lib/aiAdmin';

import { AliasesTab } from './AliasesTab';
import { CallsTab } from './CallsTab';
import { ChangesTab } from './ChangesTab';
import { Loading, LoadFailed, useLoad, useOrganizations } from './common';
import { DenyRulesTab } from './DenyRulesTab';
import { KeysTab } from './KeysTab';
import { LimitsTab } from './LimitsTab';
import { ModelsTab } from './ModelsTab';
import { OrganizationsTab } from './OrganizationsTab';
import { OverviewTab } from './OverviewTab';
import { UsageTab } from './UsageTab';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'models', label: 'Models' },
  { id: 'keys', label: 'Keys' },
  { id: 'aliases', label: 'Aliases' },
  { id: 'deny-rules', label: 'Deny rules' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'calls', label: 'Calls' },
  { id: 'limits', label: 'Limits' },
  { id: 'usage', label: 'Usage' },
  { id: 'changes', label: 'Changes' },
] as const;

export function AiPage() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((option) => option.id === params.get('tab'))?.id ?? 'overview';
  const routes = useLoad(async () => (await aiApi.routes()).routes ?? [], []);
  const orgs = useOrganizations();
  const modelIds = [...new Set((routes.data ?? []).map((route) => route.modelId))];

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader title="AI" description="Calls to AI models for every organization, and the controls for them. Each change has an audit row." />
      <Tabs value={tab} onValueChange={(value) => setParams(value === 'overview' ? {} : { tab: value }, { replace: true })}>
        <div className="max-w-full overflow-x-auto">
          <TabsList>
            {TABS.map((option) => (
              <TabsTrigger key={option.id} value={option.id}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="overview">
          <OverviewTab routes={routes.data} />
        </TabsContent>
        <TabsContent value="models">
          {routes.error ? (
            <LoadFailed message={routes.error} onRetry={routes.reload} />
          ) : !routes.data ? (
            <Loading label="Loading models" />
          ) : (
            <ModelsTab routes={routes.data} onRoutes={routes.setData} reload={routes.reload} />
          )}
        </TabsContent>
        <TabsContent value="keys">
          <KeysTab routes={routes.data} routesFailed={Boolean(routes.error)} />
        </TabsContent>
        <TabsContent value="aliases">
          <AliasesTab modelIds={modelIds} />
        </TabsContent>
        <TabsContent value="deny-rules">
          <DenyRulesTab />
        </TabsContent>
        <TabsContent value="organizations">
          <OrganizationsTab />
        </TabsContent>
        <TabsContent value="calls">
          <CallsTab />
        </TabsContent>
        <TabsContent value="limits">
          <LimitsTab orgs={orgs} />
        </TabsContent>
        <TabsContent value="usage">
          <UsageTab orgs={orgs} />
        </TabsContent>
        <TabsContent value="changes">
          <ChangesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
